import * as React from 'react';
import { clsx } from '@mentimeter/ragnar-tailwind-config';
import { type LinkT, Link } from '../link';
import { Tooltip } from '../tooltip';
import {
  variants,
  sizeVariants,
  type BreadcrumbVariantProps,
  type BreadcrumbSizeVariantProps,
} from './Breadcrumbs.variants';

interface BreadcrumbProps
  extends Omit<LinkT, 'children'>,
    Pick<BreadcrumbVariantProps, 'capitalize'>,
    Pick<BreadcrumbSizeVariantProps, 'size'> {
  children: string;
  id: string;
  isActivePath?: boolean;
  isRoot?: boolean;
  className?: string;
}

export const Breadcrumb = ({
  size,
  children,
  id,
  underline = false,
  capitalize,
  isActivePath,
  isRoot,
  className,
  ...props
}: BreadcrumbProps) => {
  const [isEllipsisActive, setIsEllipsisActive] = React.useState(false);
  const spanRef = React.useRef<HTMLSpanElement>(null);

  React.useEffect(() => {
    const span = spanRef.current;
    if (span) {
      setIsEllipsisActive(span.offsetWidth < span.scrollWidth);
    }
  }, [children]);

  return (
    <Tooltip
      trigger={
        <Link
          ref={spanRef}
          truncate
          data-testid={`breadcrumb-link-${Number(id) > 0 ? id : 'top-folder'}`}
          underline={underline}
          className={clsx([
            'block',
            'transition-colors',
            'transition-opacity',
            'duration-200',
            'ease-in',
            'truncate',
            variants({ isActivePath, capitalize }),
            sizeVariants({ size }),
            className,
          ])}
          {...props}
        >
          {children}
        </Link>
      }
      data-testid="breadcrumb-tooltip"
      side="bottom"
      delayDuration={0}
    >
      {isEllipsisActive && children}
    </Tooltip>
  );
};
